import { Box, Divider, IconButton, SvgIcon, Tooltip } from "@mui/material"
import {
  gridDimensionsSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-pro"
import { StudentAcademicStatusDTO } from "./studentAcademicStatus.dto"
import { StudentSnapshotHeader } from "./studentSnapshotHeader"
import { ManageProgramsStrip } from "./manageProgramsStrip"
import { StudentContentSnapshot } from "./studentContentSnapshot"
import { StudentAttendanceQuickLook } from "./studentAttendanceQuickLook"
import appIcons from "@utils/appIcons"

// The inline student snapshot: everything staff need about one student,
// expanded directly under their row — no overlay, no context loss. The campus
// profile is reachable through the row/kebab impersonation paths; the panel
// carries no extra affordance for it.
export function StudentDetailPanel(props: {
  student: StudentAcademicStatusDTO
}) {
  const { student } = props
  // Detail panels span the grid's TOTAL column width, which can be far wider
  // than the visible viewport — without pinning, the panel's right side lives
  // off-screen. Sticky + viewport width keeps the whole snapshot in view.
  const apiRef = useGridApiContext()
  const dimensions = useGridSelector(apiRef, gridDimensionsSelector)
  // Outer size, not inner: viewportInnerSize excludes the pinned column
  // sections, which left a dead zone right of the panel on wide screens. But
  // the outer size INCLUDES the vertical scrollbar gutter, and a panel that
  // wide overflows the scroller's content box by exactly scrollbarSize —
  // that was the horizontal scrollbar Mayer saw at 2000px with classic
  // (non-overlay) scrollbars. Subtract it whenever the grid scrolls.
  const panelWidth =
    dimensions.viewportOuterSize.width -
    (dimensions.hasScrollY ? dimensions.scrollbarSize : 0)
  // Dashboard hard rule: the panel never exceeds one window and NEVER scrolls
  // vertically — overflow scrolls INSIDE the tile bodies, whose height is
  // whatever share of the budget the tile row hands them. The budget is
  // window-based on purpose: the grid's viewportInnerSize shrinks with the
  // row count (a one-row search result collapsed it to a sliver that silently
  // clipped whole tiles). 280px ≈ page chrome above the grid; the 480px floor
  // keeps every tile legible on short windows. `height`, not `maxHeight`:
  // the grid reserves this exact budget via getDetailPanelHeight (a fixed
  // number, because auto-height re-measuring caused CLS at the
  // virtualization boundary) — the panel must fill what was reserved or a
  // short panel leaves a dead gap above the next row. Keep the formula in
  // sync with studentsTable.grid.tsx.
  return (
    <Box
      data-student-detail-panel
      sx={{
        backgroundColor: "#fafaf7",
        borderTop: "1px solid #eee",
        borderBottom: "1px solid #eee",
        position: "sticky",
        left: 0,
        width: panelWidth > 0 ? panelWidth : "100%",
        height: "max(480px, calc(100vh - 280px))",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flexShrink: 0, position: "relative" }}>
        <StudentSnapshotHeader student={student} />
        {/* Locality of behaviour: the collapse affordance lives ON the panel
            it collapses (the old toolbar Collapse button sat a viewport away
            from its subject). Same single-expand semantics, same grid API. */}
        <Tooltip title="Collapse this panel">
          <IconButton
            size="small"
            aria-label="Collapse panel"
            onClick={() => apiRef.current.setExpandedDetailPanels([])}
            sx={{ position: "absolute", top: "12px", right: "12px" }}
          >
            <SvgIcon
              component={appIcons.xClose}
              inheritViewBox
              sx={{
                width: "18px",
                height: "18px",
                fill: "none",
                stroke: "currentColor",
              }}
            />
          </IconButton>
        </Tooltip>
        <Divider />
      </Box>
      {/* The tile row takes ALL the height the header leaves (the old
          "Full campus profile" footer row is gone; its space belongs to the
          tiles). Each tile wrapper is a column so the tile inside can pin its
          header and scroll its body within its share of the budget. */}
      <Box
        sx={{
          display: "flex",
          flexWrap: { xs: "wrap", md: "nowrap" },
          flex: "1 1 auto",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {/* Proportional growth (2:2:1), not equal growth: the chip and bar
            tiles can use spare width, the four attendance stats cannot — at
            2000px equal growth handed Attendance ~580px of dead space. */}
        <Box
          sx={{
            flex: "2 1 380px",
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ManageProgramsStrip
            studentId={student.studentId}
            studentName={student.contactDetails.name}
          />
        </Box>
        <Box
          sx={{
            flex: "2 1 320px",
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            borderLeft: { md: "1px solid #eee" },
          }}
        >
          <StudentContentSnapshot studentId={student.studentId} />
        </Box>
        {/* Attendance is pooled across ALL the student's active offerings and
            resolved server-side by studentId, so the tile no longer hinges on
            the single primary registration — it renders for every student and
            hides itself when the viewer lacks the attendance roles. */}
        <Box
          sx={{
            flex: "1 1 240px",
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            borderLeft: { md: "1px solid #eee" },
          }}
        >
          <StudentAttendanceQuickLook studentId={student.studentId} />
        </Box>
      </Box>
    </Box>
  )
}
